/* eslint-disable no-promise-executor-return */
import React, { Dispatch, SetStateAction, useContext, useRef, useState } from "react";
import { Button } from "@mui/material";
import { SuccessAlertContext } from "App";
import { CSVCountCheckCsvStock, CSVThresholdCheckStock } from "../types";
import SupplierConfirmTable from "./SupplierConfirmTable";
import getCsvConfirmApi from "../api/getCsvConfirmApi";
import CsvUploadModal from "./CsvUploadModal";
import getCsvThresholdConfirmApi from "../api/getCsvThresholdConfirmApi";
import getCsvConfirmCheckApi from "../api/getCsvConfirmCheckApi";
import getCsvThresholdConfirmCheckApi from "../api/getCsvThresholdConfirmCheckApi";
import uploadCountCsv from "../api/uploadCountCsv";
import updateThresholdCsv from "../api/uploadThresholdCsv";
import SupplierConfirmCsvTable from "./SupplierConfirmCsvTable";

const limit = 10;

/**
 * 確認画面でのアクションボタンたち
 * @param onClose モーダルを閉じる
 * @param onCsvUpload アップロードする
 * @param disabled 非活性
 * @returns JSX.Element
 */
function ActionButtons(onClose: Function, onCsvUpload: Function, disabled: boolean) {
  return (
    <>
      <Button variant="custom-outlined" onClick={() => onClose()}>
        キャンセル
      </Button>
      <Button variant="contained" onClick={() => onCsvUpload()} disabled={disabled}>
        登録
      </Button>
    </>
  );
}

type Props = {
  refresh: Function; // 在庫を登録した後に読み込み直す
  isOpenStockCsvUpload: boolean;
  setIsOpenStockCsvUpload: Dispatch<SetStateAction<boolean>>;
  isOpenThresholdCsvUpload: boolean;
  setIsOpenThresholdCsvUpload: Dispatch<SetStateAction<boolean>>;
  supplierCd?: string;
  storehouseCd?: string;
};

/**
 * CSVアップロードボタン
 * @param root0 コンポーネントのプロップス
 * @returns JSX.Element
 */
export default function CsvModals({
  refresh,
  isOpenStockCsvUpload,
  setIsOpenStockCsvUpload,
  isOpenThresholdCsvUpload,
  setIsOpenThresholdCsvUpload,
  supplierCd,
  storehouseCd,
}: Props) {
  const [isCheck, setIsCheck] = useState(false); // 確認画面かどうか
  const [file, setFile] = useState<File | null>(null);
  const [csvStockList, setCsvStockList] = useState<CSVCountCheckCsvStock[]>([]);
  const [csvThresholdList, setCsvThresholdList] = useState<CSVThresholdCheckStock[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isMoreLoading, setIsMoreLoading] = useState(false);
  const [offset, setOffset] = useState(0);
  const isFastLoading = useRef(false);
  const [processId, setProcessId] = useState("");
  const [initCheckScreen, setInitCheckScreen] = useState(false);
  const [total, setTotal] = useState(0);
  const [confirmError, setConfirmError] = useState("");

  const { showAlert } = useContext(SuccessAlertContext);

  const onClose = () => {
    setTimeout(() => {
      setIsOpenStockCsvUpload(false);
      setIsOpenThresholdCsvUpload(false);
      setCsvStockList([]);
      setCsvThresholdList([]);
      setIsCheck(false);
      setFile(null);
      setOffset(0);
      setProcessId("");
      setInitCheckScreen(false);
      setTotal(0);
      setIsLoading(false);
      setConfirmError("");
      isFastLoading.current = false;
    }, 200);
  };

  const onCsvStockUpload = async () => {
    if (file) {
      setIsLoading(true);
      try {
        // CSV反映
        await uploadCountCsv(processId);
        onClose();
        showAlert("在庫を登録しました。");
        refresh();
      } catch (err) {
        console.error(err);
        showAlert("在庫数csvアップロードに失敗しました。", "error");
      } finally {
        setIsLoading(false);
      }
    } else {
      showAlert("予期せぬエラーが発生しました。", "error");
    }
  };

  const onCsvThresholdUpload = async () => {
    if (file) {
      setIsLoading(true);
      try {
        // CSV反映
        await updateThresholdCsv(processId);
        onClose();
        showAlert("設定値を登録しました。");
        refresh();
      } catch (err) {
        console.error(err);
        showAlert("設定値csvアップロードに失敗しました。", "error");
      } finally {
        setIsLoading(false);
      }
    }
  };

  // 在庫設定値確認画面へ
  const goThresholdConfirm = async () => {
    if (file) {
      setIsLoading(true);
      try {
        const csvData = await getCsvThresholdConfirmApi({
          // user_id: localStorage.getItem("user_id") ?? '100',
          code: supplierCd ?? "",
          offset: 0,
          limit,
          csv_file: file,
        });
        setCsvThresholdList(csvData.stocks);
        setTotal(csvData.total);
        setProcessId(csvData.processId);
        setOffset(csvData.stocks.length);
        setConfirmError(csvData.error ?? "");
        setIsCheck(true);
      } catch (err) {
        console.error(err);
        showAlert("設定値CSVの確認データ取得に失敗しました。", "error");
      } finally {
        setIsLoading(false);
      }
    }
  };

  // 在庫設定値画面追加読み込み
  const getMoreThresholdStock = async () => {
    if (initCheckScreen && total > offset) {
      setIsMoreLoading(true);
      isFastLoading.current = true;
      try {
        const csvData = await getCsvThresholdConfirmCheckApi({
          code: supplierCd ?? "",
          process_id: processId,
          offset,
          limit,
        });
        setOffset((prev) => prev + csvData.length);
        setCsvThresholdList((prev) => [...prev, ...csvData]);
      } catch (err) {
        console.error(err);
        showAlert("設定値CSVの確認データ取得に失敗しました。", "error");
      } finally {
        setIsMoreLoading(false);
        isFastLoading.current = false;
      }
    }
  };

  // 在庫登録確認画面へ
  const goStockConfirm = async () => {
    if (file) {
      setIsLoading(true);
      try {
        const csvData = await getCsvConfirmApi({
          // user_id: localStorage.getItem("user_id") ?? '100',
          code: supplierCd ?? storehouseCd ?? "",
          offset: 0,
          limit,
          csv_file: file,
        });
        setCsvStockList(csvData.stocks);
        setTotal(csvData.total);
        setProcessId(csvData.processId);
        setOffset(csvData.stocks.length);
        setConfirmError(csvData.error ?? "");
        setIsCheck(true);
      } catch (err) {
        console.error(err);
        showAlert("在庫登録CSVの確認データ取得に失敗しました。", "error");
      } finally {
        setIsLoading(false);
      }
    }
  };

  // 在庫登録画面追加読み込み
  const getMoreCheckStock = async () => {
    if (initCheckScreen && total > offset) {
      setIsMoreLoading(true);
      isFastLoading.current = true;
      try {
        const csvData = await getCsvConfirmCheckApi({
          code: supplierCd ?? storehouseCd ?? "",
          process_id: processId,
          offset,
          limit,
        });
        setOffset((prev) => prev + csvData.length);
        setCsvStockList((prev) => [...prev, ...csvData]);
      } catch (err) {
        console.error(err);
        showAlert("在庫登録CSVの確認データ取得に失敗しました。", "error");
      } finally {
        isFastLoading.current = false;
        setIsMoreLoading(false);
      }
    }
  };

  return (
    <>
      <CsvUploadModal
        isOpenCsvUpload={isOpenStockCsvUpload}
        isCheck={isCheck}
        onClose={onClose}
        goConfirm={goStockConfirm}
        file={file}
        title="在庫数CSVアップロード"
        setFile={setFile}
        isLoading={isLoading}
      />
      <SupplierConfirmCsvTable
        open={isOpenStockCsvUpload && isCheck}
        onClose={onClose}
        stockList={csvStockList}
        ActionButtons={ActionButtons(onClose, onCsvStockUpload, isLoading || !!confirmError)}
        title="在庫数CSVアップロード確認"
        content="以下の在庫を登録します。よろしいですか？"
        isFastLoading={isFastLoading}
        getMoreStockList={getMoreCheckStock}
        setInit={setInitCheckScreen}
        isLoading={isMoreLoading}
        error={confirmError}
        errorTitle="以下のCSVデータにエラーがあります。ご確認ください。"
        isSupplier={!!supplierCd} // 仕入先かどうか
      />
      <CsvUploadModal
        isOpenCsvUpload={isOpenThresholdCsvUpload}
        isCheck={isCheck}
        onClose={onClose}
        goConfirm={goThresholdConfirm}
        file={file}
        title="設定値CSVアップロード"
        setFile={setFile}
        isLoading={isLoading}
      />
      <SupplierConfirmTable
        open={isOpenThresholdCsvUpload && isCheck}
        onClose={onClose}
        stockList={csvThresholdList}
        ActionButtons={ActionButtons(onClose, onCsvThresholdUpload, isLoading || !!confirmError)}
        title="設定値CSVアップロード確認"
        content="以下の在庫の設定値を登録します。よろしいですか？"
        isFastLoading={isFastLoading}
        getMoreStockList={getMoreThresholdStock}
        setInit={setInitCheckScreen}
        isLoading={isMoreLoading}
        isThreshold
        error={confirmError}
        errorTitle="以下のCSVデータにエラーがあります。ご確認ください。"
      />
    </>
  );
}
